/**
 * Channel registry — single source for supported integration kinds across
 * onboarding, settings, and inbox tab mapping.
 */
import { Mail, MessageCircle, Send, Linkedin, MessageSquare, Smartphone, HelpCircle, Facebook, Instagram } from "lucide-react";

/** Full channel list for Settings → Connections cards. */
export const SETTINGS_CHANNEL_KINDS = [
  {
    kind: "gmail",
    label: "Gmail",
    Icon: Mail,
    iconBg: "bg-rose-100 text-rose-700",
    tooltip: "Connect with Google OAuth.",
  },
  {
    kind: "whatsapp",
    label: "WhatsApp",
    Icon: MessageCircle,
    iconBg: "bg-emerald-100 text-emerald-700",
    tooltip: "Pair your WhatsApp number via OpenClaw QR code.",
  },
  {
    kind: "telegram",
    label: "Telegram",
    Icon: Send,
    iconBg: "bg-cyan-100 text-cyan-700",
    tooltip: "Connect a Telegram bot with a BotFather token.",
  },
  {
    kind: "reddit",
    label: "Reddit",
    Icon: MessageCircle,
    iconBg: "bg-orange-100 text-orange-700",
    tooltip: "Connect your Reddit account via OAuth.",
  },
  {
    kind: "quora",
    label: "Quora",
    Icon: HelpCircle,
    iconBg: "bg-red-100 text-red-700",
    tooltip: "Connect your Quora profile URL.",
  },
  {
    kind: "facebook",
    label: "Facebook",
    Icon: Facebook,
    iconBg: "bg-blue-100 text-blue-700",
    tooltip: "Connect Facebook Page Messenger via Facebook Login.",
    hidden: true,
  },
  {
    kind: "instagram",
    label: "Instagram",
    Icon: Instagram,
    iconBg: "bg-pink-100 text-pink-700",
    tooltip: "Connect Instagram DMs via Instagram Login (not Facebook).",
    hidden: true,
  },
  {
    kind: "slack",
    label: "Slack",
    Icon: MessageSquare,
    iconBg: "bg-violet-100 text-violet-700",
    tooltip: "Coming in Phase B (Slack workspace)",
    deferred: true,
  },
  {
    kind: "sms",
    label: "SMS",
    Icon: Smartphone,
    iconBg: "bg-slate-200 text-slate-800",
    tooltip: "Coming in a later sprint (Twilio SMS)",
    deferred: true,
  },
  {
    kind: "linkedin",
    label: "LinkedIn",
    Icon: Linkedin,
    iconBg: "bg-sky-100 text-sky-700",
    tooltip: "LinkedIn profile connection — not in this release",
    deferred: true,
  },
];

/** Channels shown on Settings → Connections (active integrations only). */
export const SETTINGS_CONNECTION_KINDS = SETTINGS_CHANNEL_KINDS.filter((c) => !c.deferred && !c.hidden);

/** Kinds auto-seeded during onboarding. */
export const ONBOARDING_CHANNEL_KINDS = ["whatsapp", "telegram", "gmail", "reddit", "quora"];

/** WhatsApp uses OpenClaw QR pairing. */
export const PAIRING_CHANNEL_KINDS = new Set(["whatsapp"]);

/** Telegram uses bot-token configure API. */
export const TELEGRAM_CONFIGURE_KINDS = new Set(["telegram"]);

/** Reddit uses OAuth redirect. */
export const REDDIT_OAUTH_KINDS = new Set(["reddit"]);

/** Meta (Facebook Page + Instagram) uses OAuth redirect. */
export const META_OAUTH_KINDS = new Set(["facebook", "instagram"]);

/** Quora uses profile URL configure API. */
export const QUORA_CONFIGURE_KINDS = new Set(["quora"]);

/** LinkedIn uses profile URL configure API. */
export const LINKEDIN_CONFIGURE_KINDS = new Set(["linkedin"]);

/** Channels with a configure/pair action on Settings → Connections. */
export const SETTINGS_CONFIGURABLE_KINDS = new Set([
  "gmail",
  ...PAIRING_CHANNEL_KINDS,
  ...TELEGRAM_CONFIGURE_KINDS,
  ...REDDIT_OAUTH_KINDS,
  ...META_OAUTH_KINDS,
  ...QUORA_CONFIGURE_KINDS,
]);

export function isSettingsConfigurableKind(kind) {
  return SETTINGS_CONFIGURABLE_KINDS.has(kind);
}

export const CONNECTION_LABELS = Object.fromEntries(
  SETTINGS_CHANNEL_KINDS.map((c) => [c.kind, c.label]),
);
